import React, { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  DOCUMENT_LABELS,
  TrashItem,
  fetchTrash,
  purgeDocument,
  restoreDocument,
} from "@/lib/documents/deletion";
import VersionHistoryDialog from "@/components/Documents/VersionHistoryDialog";

interface TrashPanelProps {
  projectId: string;
  onChanged?: () => void | Promise<void>;
}

/** Papperskorg per projekt: återställ eller radera permanent. */
export const TrashPanel: React.FC<TrashPanelProps> = ({ projectId, onChanged }) => {
  const [items, setItems] = useState<TrashItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await fetchTrash(projectId));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (item: TrashItem, mode: "restore" | "purge") => {
    setBusyId(item.id);
    try {
      const target = { type: item.type, id: item.id, name: item.name, projectId };
      if (mode === "restore") {
        await restoreDocument(target);
        toast.success("Dokumentet är återställt");
      } else {
        await purgeDocument(target);
        toast.success("Dokumentet är permanent raderat");
      }
      await load();
      await onChanged?.();
    } catch (e) {
      toast.error("Åtgärden misslyckades", {
        description: e instanceof Error ? e.message : undefined,
      });
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <p className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Hämtar papperskorgen…
      </p>
    );
  }

  if (items.length === 0) {
    return (
      <Card className="p-8 text-center text-sm text-muted-foreground">
        Papperskorgen är tom. Borttagna dokument hamnar här och kan återställas.
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {items.map((item) => (
        <Card key={item.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="truncate font-medium">{item.name}</span>
              <Badge variant="secondary">{DOCUMENT_LABELS[item.type]}</Badge>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              Borttaget {new Date(item.deleted_at).toLocaleDateString("sv-SE")}
              {item.delete_reason ? ` – ${item.delete_reason}` : ""}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <VersionHistoryDialog
              compact
              target={{ type: item.type, id: item.id, name: item.name, projectId }}
              onRestored={load}
            />
            <Button
              variant="outline"
              size="sm"
              disabled={busyId === item.id}
              onClick={() => act(item, "restore")}
            >
              <RotateCcw className="mr-2 h-4 w-4" /> Återställ
            </Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={busyId === item.id}
              onClick={() => act(item, "purge")}
            >
              <Trash2 className="mr-2 h-4 w-4" /> Radera permanent
            </Button>
          </div>
        </Card>
      ))}
    </div>
  );
};

export default TrashPanel;
